/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import get from 'lodash/get';
import invoke from 'lodash/invoke';
import map from 'lodash/map';
import Highlighter from 'react-highlight-words';
import { useSelector } from 'react-redux';

import { icons } from '../../../../assets';
import { DropDownFilter, EllipseText, Input } from '../../../../components/common';
import { DropDownRenderTypeEnum } from '../../../../components/common/DropDownFilter/DropDownFilter';
import { EInputType, IInputProps } from '../../../../components/common/Input/Input';
import { makeSelectTranslationCommon } from '../../../internationalization/intSelectors';
import { useTableFilterApi, useTableFilterSuggestApi } from '../../api';
import { useTableFilter } from './hooks/useTableFilter';
import {
    FilterSearchInputDropdownContainerStyled,
    FilterSearchInputDropdownItemStyled,
    FilterSearchInputDropdownItemTextStyled,
} from './styled/TableFilterInputSearchStyled';


interface ITableFilterInputSearch extends Partial<IInputProps> {
    name: string;
}

const TableFilterInputSearch = ({ name, ...input }: ITableFilterInputSearch) => {

    const translationCommon = useSelector(makeSelectTranslationCommon());

    const [currentValue, setCurrentValue] = useState('');

    const dropDownRef = useRef(null);

    /* API */

    const apiFilter = useTableFilterApi();
    const apiSuggest = useTableFilterSuggestApi();

    const { filters, selectedFilter } = useTableFilter(name);

    useEffect(() => {
        setCurrentValue(selectedFilter?.value ?? '');
    }, [selectedFilter]);

    useEffect(() => {
        const dropdownTooltipShown = get(dropDownRef, 'current.state.isToolTipShown');
        if ((apiSuggest.result?.length ?? 0) && !dropdownTooltipShown) {
            invoke(dropDownRef, 'current.toggleToolTip');
        }
    }, [apiSuggest.result]);

    const filterSearchInputDropdownClass = classNames(
        'filter__dropdown',
        apiSuggest.result?.length ? '' : 'no-items',
    );

    const onInputSearch = (event: ChangeEvent<HTMLInputElement>) => {
        setCurrentValue(event.target.value);
        if (event.target.value !== selectedFilter?.value) {
            apiSuggest?.getSuggest(name, event.target.value);
        }
    };

    const onCloseClick = () => {
        if (currentValue !== selectedFilter?.value && currentValue !== '' || (currentValue === '' && !!selectedFilter?.value)) {
            apiFilter.filter({ ...filters, [name]: { value: currentValue } });
        }
    };

    const onEnterHandler = () => {
        if (currentValue !== selectedFilter?.value) {
            invoke(dropDownRef, 'current.closeTooltip');
            apiFilter.filter({ ...filters, [name]: { value: currentValue } });
        }
    };

    const onItemClick = (value: string) => {
        invoke(dropDownRef, 'current.closeTooltip');
        apiFilter.filter({ ...filters, [name]: { value } });
    };

    const inputProps = { ...input, value: currentValue || '' };

    const renderDropDownItems = () =>
        map(apiSuggest.result, (result: string) =>
            <FilterSearchInputDropdownItemStyled key={result} onClick={() => onItemClick(result)}>
                <EllipseText>
                    <FilterSearchInputDropdownItemTextStyled>
                        <Highlighter
                            autoEscape
                            highlightClassName="dropdown-item_highlight"
                            searchWords={[currentValue || '']}
                            textToHighlight={result}
                        />
                    </FilterSearchInputDropdownItemTextStyled>
                </EllipseText>
            </FilterSearchInputDropdownItemStyled>,
        );

    return (
        <DropDownFilter
            renderInBody
            showCloseButton
            closeOnFilterButtonClick={false}
            filterButtonLabel={currentValue}
            icon={icons.searchSmallIcon}
            ref={dropDownRef}
            render={() => <FilterSearchInputDropdownContainerStyled
                className={filterSearchInputDropdownClass}/>
            }
            renderButton={() => <Input
                {...({ placeholder: translationCommon?.searchInput.placeholder ?? '', ...inputProps })}
                className="drop-down__input"
                righticon={icons.searchSmallIcon}
                righticonhandler={onEnterHandler}
                type={EInputType.small}
                onChange={onInputSearch}
                onEnter={onEnterHandler}
            />}
            renderType={DropDownRenderTypeEnum.input}
            onCloseClick={onCloseClick}
        >
            {
                renderDropDownItems()
            }
        </DropDownFilter>
    );
};

export default memo(TableFilterInputSearch);
